/**
 * **SIDES — the frozen index → colour → name table.**
 *
 * S12 plan row **R1 `SIDES-CORE`**, over S11 plan **spec 3** ("SIDES: the index
 * → colour → name table, and where it lives") with amendments 3.1–3.4, and Ivy's
 * `design/wr-s11-d3a-sides-pins.md` **§1**, which pins the four rows.
 *
 * ── Why `@wr/contracts` and not `@wr/game-core`, `@wr/ui` or a new package ───
 *
 * Spec 3: *"exported from **the lowest package both `apps/game` and `apps/web`
 * already depend on** — `packages/contracts` unless the builder finds an existing
 * shared-constants home … It may NOT live in `packages/ui` if `apps/game` cannot
 * import it."*
 *
 * Measured against the shipped manifests rather than assumed:
 *
 * | package | in `apps/game` deps | in `apps/web` deps |
 * |---|---|---|
 * | `@wr/contracts` | yes | yes |
 * | `@wr/game-core` | yes | yes — **and it depends on `@wr/contracts`** |
 * | `@wr/ui` | **no** | yes |
 * | `@wr/i18n` | **no** | yes |
 *
 * So both apps already depend on two candidates, and of those two
 * `@wr/contracts` is strictly the lower: `@wr/game-core`'s own `package.json`
 * lists `@wr/contracts` as its only dependency. `@wr/ui` is excluded exactly as
 * spec 3 anticipated — `apps/game` cannot import it — and that exclusion is a
 * fact about the manifest, not a preference.
 *
 * This is also the **existing shared-constants home** spec 3 leaves room for:
 * `engines.ts` (`ENGINE_KEYS`, §16f A54) and `legal.ts` are already non-payload
 * constants living here for the same stated reason — *"this package is zod-only
 * and every tier may already depend on it"*. `SIDES` is a third of the same
 * kind: a plain frozen `const`, no zod, no runtime dependency, no Colyseus.
 *
 * ── What is in the table, and what is deliberately NOT ──────────────────────
 *
 * `{ sideIndex, colorIndex, nameKey }`, and nothing else.
 *
 *  - **The colour cell is a `colorIndex` into the existing six-token rotation
 *    (`IDENTITY_COLOURS` in `@wr/ui`, `IDENTITY_COLOUR_COUNT` in
 *    `@wr/game-core`), never a raw CSS token** (spec 3). *"Two colour
 *    vocabularies are free to disagree; one is not."* With one vocabulary the
 *    invariant below is decidable and cheap.
 *  - **The name cell is a copy-slot KEY, never a literal** (spec 3). The four
 *    strings live in `@wr/i18n`'s catalogue, are Theo's and Nora-gated, and this
 *    package does not — and must not — depend on `@wr/i18n` to hold a key.
 *  - **No hex, no token name, no CSS.** Amendment 3.4 rule 3: zero new colour
 *    tokens, zero tints, zero shades. Nothing here can express one.
 *
 * ── The invariant this table exists to make decidable ───────────────────────
 *
 * > **For every seated player in a team room:
 * > `seat.colorIndex === SIDES[seat.sideIndex].colorIndex`.**
 *
 * It is a statement about **values**, and (Ivy §7.2, adopted as amendment 3.2)
 * it only bites because every colour-bearing element on a seat reads from that
 * one value — true by construction since T-493 took repo-wide synthesised-id
 * `Avatar` callers to 0 behind a compiler-enforced discriminated union. **If any
 * future component takes a bed, ring, bar or chip from anything other than the
 * seat's `colorIndex`, that hole reopens and this table will not tell you.**
 *
 * ── The derive chain, named end to end (T-510 Q-F6) ─────────────────────────
 *
 * The seat's `colorIndex` is **written** in exactly four places, all server-side
 * (`BaseGameRoom.onJoin`, `bot-seating.seatBots`, `onLeave`'s `filling` release,
 * `createEmptySeat`), and every one of them writes a matching `sideIndex`.
 * Everything downstream is a **pass-through read** — it copies the seat's value
 * and takes no view on sides. Named here once so a future reader inherits the
 * whole chain rather than re-deriving it:
 *
 *  - server-side derives: `BaseGameRoom.publishMetadata`'s lobby seat preview,
 *    `WordSearchRoom.publishClaim` (this is why the board is free),
 *    `LobbyRoom`'s copy and its comparator;
 *  - `packages/game-core/src/lobby-state.ts`'s `lobbyBodyFromState`, the hop
 *    between the room's metadata and the lobby payload;
 *  - the client copies: `apps/web/src/hooks/useGameRoom.ts`,
 *    `apps/web/src/lib/room.ts`'s view models, and
 *    `apps/web/src/game/GameWrapper.tsx`.
 *
 * **None of them writes or derives a colour**, which is the property that makes
 * the fence above decidable at the four write sites alone.
 */
/**
 * **THE PIN — the four side rows, in this order. Frozen** (D3a §1).
 *
 * | `sideIndex` | `colorIndex` | token | the word |
 * |---|---|---|---|
 * | 0 | 3 | `play` (periwinkle) | **Blue** |
 * | 1 | 0 | `warn` (coral) | **Red** |
 * | 2 | 1 | `good` (mint) | **Green** |
 * | 3 | 4 | `lilac` | **Purple** |
 *
 * **Not used by sides: `colorIndex` 2 (`coin`) and 5 (`sand`).** The FFA
 * per-player rotation is unchanged and still uses all six — this reserves
 * nothing out of the rotation, it only fixes which four rows `SIDES` names.
 *
 * Two of Ivy's reasons are worth carrying at the code, because they are the ones
 * a well-meaning re-order would break:
 *
 *  - **`coin` cannot be a side colour** — it is the product's *money* colour
 *    (the gold numerals, `.wr-prize-chip`, `.wr-coin-dot`, the gold band in
 *    `--wr-focus`), and the results screen is where a gold side row would sit on
 *    the same row track as a gold prize chip. *"Money cannot be carried by
 *    something other than money."*
 *  - **Side 0 is blue and side 1 is red, and it matters** — side index is not
 *    viewer-relative, so blue-first means the fixed *first* side never wears the
 *    error colour, and `play` vs `warn` is the widest hue separation the palette
 *    can produce at comparable lightness. Sides 0–1 are the **only** two that
 *    exist in the launch matrix (1v1 and 2v2): *"sides 2–3 must be adequate;
 *    sides 0–1 must be excellent."*
 *
 * **`side.3.name` is "Purple".** The D1 copy deck's first draft said `"Yellow"`
 * and records its own supersession: a build finding `"Yellow"` is reading a
 * superseded source.
 */
export const SIDES = [
    { sideIndex: 0, colorIndex: 3, nameKey: "side.0.name" },
    { sideIndex: 1, colorIndex: 0, nameKey: "side.1.name" },
    { sideIndex: 2, colorIndex: 1, nameKey: "side.2.name" },
    { sideIndex: 3, colorIndex: 4, nameKey: "side.3.name" },
];
/**
 * **Four is the ceiling and it is ENFORCED, not documented** (spec 3).
 *
 * Derived from the table's own length so the two can never disagree: there is no
 * "Team Ochre" path because there is no fifth row to reach.
 */
export const MAX_SIDES = SIDES.length;
/**
 * Two is the floor. A room with one team (or none) has no sides at all — that is
 * a free-for-all shape, where colour is per-player and the table does not apply.
 *
 * It is not the **only** free-for-all shape: after A132 a **royale**
 * (`team_count > 4 × team_size 1`) is one too. The floor and the ceiling are
 * both necessary and neither is sufficient on its own — see
 * {@link roomHasSides}, which is the single place the whole rule lives.
 */
export const MIN_SIDES = 2;
/**
 * **The wire sentinel for "this seat has no side"** — spec 1's convention,
 * minted for `avatarUrl` and reused here verbatim: *"sentinel-at-schema /
 * null-at-contract … No third pattern."*
 *
 * A Colyseus number is not nullable, so the schema carries `-1`; a boundary that
 * hands the value to a contract or a view model maps it through
 * {@link sideIndexFromWire}. `-1` is also what an **empty** slot carries, which
 * is the same statement (`teamIndex` already uses `-1` the same way).
 */
export const NO_SIDE = -1;
/**
 * **Does this room have sides at all?** — the predicate A132 rewrote.
 *
 * > *"A room has sides when its seats are partitioned into `2 … 4` camps that
 * > the frozen table can name and colour. A partition into more than four camps
 * > is a side structure **only if a camp can hold more than one player**; a
 * > partition of `N > 4` single-seat camps is not a side structure at all — it
 * > is a **free-for-all**."* (A132 §1, the ruling.)
 *
 * The whole truth table, because the third row is the one that moved and the
 * fourth is the one that did not:
 *
 * | `team_count` | `team_size` | this answers | why |
 * |---|---|---|---|
 * | `0` or `1` | any | `false` | one camp is no camps: the FFA shape |
 * | `2 … 4` | any (incl. `1`) | `true` | the table can name and colour them; **a 1v1 has two sides** |
 * | `> 4` | `1` | `false` | every camp is one player, so *"who is with me"* is not a fact this room has — a **royale**, per-player colour |
 * | `> 4` | `> 1` | `true` | a real side structure the table cannot express → {@link assertSupportedSideCount} refuses it at spawn |
 *
 * **The reason the last two rows differ is the whole ruling, and it is one
 * sentence: colour is load-bearing for exactly one fact — *who is with me*.** In
 * a `> 4 × team_size 1` room that fact does not exist, and *"a team of one is a
 * player"* is already LOCKED and already implemented at the render layer
 * (`Room.tsx` passes `label: null` at `teamSize === 1`). In a `> 4 ×
 * team_size > 1` room the fact **does** exist and the four rows cannot express
 * it, so degrading to per-player colour would silently destroy the teammate
 * relation. Refusing is right, and stays.
 *
 * **T-508 shipped this as `teamCount >= MIN_SIDES` and A132 overruled it** on a
 * premise the build could not see from inside its ticket: §10's *"Battle Royale,
 * 10 players — Falls out. `team_count=10, team_size=1`. **One INSERT. Modes are
 * data.**"* is a standing commitment, spelled the same way in six places (this
 * package, `packages/db/src/schema/config.ts`, `packages/ui`'s `Room.tsx`,
 * `packages/config`'s two test suites, §16d), and the narrow reading made a
 * documented one-INSERT feature unshippable in the product named Wordy Royale.
 *
 * **Spec 3's wording is untouched and was not amended.** It refuses *"a room
 * config resolving to five or more **sides**"* — under this ruling a ten-seat
 * royale resolves to **no** sides, so that clause does not reach it and spec 3's
 * own next bullet (*"FFA: `sideIndex` absent … Rotation and de-collision
 * unchanged"*) is the one that does. The ceiling's slogan also stands: there is
 * still no "Team Ochre" path, because there is still no fifth row to reach.
 *
 * **The discontinuity A132 §2 accepts deliberately, named so QA does not report
 * it:** at `team_size 1`, a **four**-seat free-for-all is a side room (fixed
 * Blue/Red/Green/Purple by seat) and a **five**-seat one is not (rotation +
 * de-collision). Accepted: `1v1v1v1` with four maximally-separated camp colours
 * is better design than four rotation colours, and narrowing sides to
 * `team_size > 1` would strand table rows 2 and 3 permanently.
 */
export function roomHasSides(shape) {
    if (shape.teamCount < MIN_SIDES)
        return false;
    if (shape.teamCount <= MAX_SIDES)
        return true;
    return shape.teamSize > 1;
}
/**
 * The seat's side, from its team — {@link NO_SIDE} when the room has none.
 *
 * Total on purpose: a negative `teamIndex` is the never-occupied sentinel and
 * answers `NO_SIDE`, so an empty slot needs no special case at any call site.
 * It does **not** range-check `teamIndex` against {@link MAX_SIDES}; that is
 * {@link assertSupportedSideCount}'s job and it fires once, at spawn, rather
 * than per seat.
 *
 * **A royale seat lands here too.** In a `team_count 10 × team_size 1` room
 * `teamIndexForSeat` still answers `seatIndex % 10` — teams remain data, and the
 * participant row still records one — but {@link roomHasSides} answers `false`,
 * so every seat takes the `NO_SIDE` return and `resolveSeatColour` falls through
 * to the per-player rotation. That is the whole of A132 at the seat.
 */
export function sideIndexForTeam(teamIndex, shape) {
    if (!roomHasSides(shape))
        return NO_SIDE;
    if (teamIndex < 0)
        return NO_SIDE;
    return teamIndex;
}
/**
 * The table row for a side index, or `null`.
 *
 * Total over any number, including the {@link NO_SIDE} sentinel and a value from
 * a future server this build does not know about — a reader that has to guard
 * with `>= 0 && < 4` before every lookup is a reader that will forget once.
 */
export function sideForIndex(sideIndex) {
    return SIDES[sideIndex] ?? null;
}
/**
 * **The colour a seat on this side must wear**, or `null` when there is no such
 * side. The one function the server's colour assignment reads.
 */
export function sideColourIndex(sideIndex) {
    return sideForIndex(sideIndex)?.colorIndex ?? null;
}
/**
 * **The schema → contract half of spec 1's convention:** `-1` becomes `null`.
 *
 * It has no caller in R1 by design — R1 is *"the table and the assignment, no
 * new chrome"*, and the room's seat is the one published surface with **no REST
 * contract in front of it** (`@wr/contracts` carries `LobbySeatPreview`, and
 * spec 3 is explicit that `sideIndex` goes on the **room's** seat schema and not
 * on the lobby preview). It exists here, tested, so that R2's results split and
 * R3's room chrome map the sentinel in **one** place rather than each spelling
 * `x === -1 ? null : x` and one of them spelling it `x > 0`.
 *
 * Anything that is not a row of the table — the sentinel, a negative, a fifth
 * side from a newer server — answers `null`. Fail-closed is the required
 * direction for a mis-sequenced deploy (§16h A114 §6's precedent: *"the client
 * treats an absent value as `false`, never 'unknown, therefore probably yes'"*).
 */
export function sideIndexFromWire(sideIndex) {
    if (sideIndex === undefined || sideIndex === null)
        return null;
    return sideForIndex(sideIndex) === null ? null : sideIndex;
}
/**
 * **A room config resolving to five or more sides is refused at spawn** (spec 3,
 * *"Four is the ceiling and it is ENFORCED, not documented"*).
 *
 * A typed error rather than a string so the refusal is identifiable by the
 * catcher and by the fence, and so the two facts a human needs — how many sides
 * were asked for, and how many exist — travel with it rather than being parsed
 * back out of a message.
 */
export class UnsupportedSideCountError extends Error {
    requested;
    supported;
    constructor(requested) {
        super(`a room config resolving to ${requested} sides cannot spawn: the SIDES table has exactly ` +
            `${MAX_SIDES} rows (side 0…${MAX_SIDES - 1}) and there is no fifth colour to reach. ` +
            "Raising the ceiling needs a new identity token and a new contrast pass " +
            "(wr-s11-d3a-sides-pins.md §1 and §3), not a fifth row here.");
        this.name = "UnsupportedSideCountError";
        this.requested = requested;
        this.supported = MAX_SIDES;
    }
}
/**
 * Refuse a room whose config resolves to more sides than the table has rows.
 *
 * Called **once, at spawn**, before any seat exists — *"refused at spawn with a
 * typed error, not degraded at render time"*. A room with **no sides** is never
 * refused: it reaches no row of the table at all. After A132 that is two shapes,
 * not one — `team_count <= 1`, and the **royale** (`team_count > 4 ×
 * team_size 1`), which spawns as a free-for-all with per-player colours.
 *
 * So the refusable set is exactly `team_count > 4 AND team_size > 1`: a genuine
 * five-or-more-side structure, which is the only case where degrading to
 * per-player colour would destroy a relation the room actually has. No seeded
 * mode is in it — all three are `team_count 2` — and no operator write path for
 * `team_count` exists today, which is why A132 sends *"stop respawning a config
 * that cannot spawn"* to the ledger as `SIDES-CEILING-EXCLUDE` (a backoffice
 * config-validation concern: refuse the row at write time, not the room at spawn
 * time) rather than minting a ticket now.
 */
export function assertSupportedSideCount(shape) {
    if (!roomHasSides(shape))
        return;
    if (shape.teamCount > MAX_SIDES)
        throw new UnsupportedSideCountError(shape.teamCount);
}
//# sourceMappingURL=sides.js.map